import React from "react";
import "./FederalReductionsSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import TwoColumnLayout from "../components/TwoColumnLayout";
import { StatBlock } from "../components";
import FEDERAL_REDUCTIONS_BOTTOM_LEFT_IMAGE from "../assets/images/jii-section-image.png";

const FEDERAL_REDUCTIONS_STATS_TOP = [
  {
    statPrefix: "$",
    stat: 4.5,
    statLabel: "billion",
    label: "cut in discretionary spending for the Department of Labor",
  },
  {
    statPrefix: "$",
    stat: 932,
    statLabel: "million",
    label: "cut to the Employment and Training Administration ",
  },
  {
    statPrefix: "$",
    stat: 373.7,
    statLabel: "million",
    label:
      "cut in discretionary spending for the Office of Justice Partnerships",
  },
];

const FEDERAL_REDUCTIONS_STATS_BOTTOM = [
  {
    stat: 77,
    statLabel: "% +",
    label: (
      <>
        Respondents indicated that funding delays or cancellations have affected
        the services they provide in their communities *
        <br />
        <br />
        <span style={{ fontWeight: 100, fontSize: 14, color: "#888" }}>
          * Most commonly, it was through fewer educational or skills training
          opportunities offered. This was followed by a reduction in advocacy or
          community engagement work, decreased access to behavioral health or
          mental health support, and decreased case management.
        </span>
      </>
    ),
  },
  {
    stat: 2750,
    statLabel: "",
    label: "Reported unserved individuals as result of lost or delayed funding",
  },
];

const FederalReductionsSection: React.FC = () => {
  const topLeftContent = (
    <>
      <SectionHeader
        title="Federal Funding Disruptions: Justice Impacted Individuals and Communities"
        description="Funding loss in workforce reentry programs"
      />
      <div className="federal-reductions-section-top-left-content">
        <p>
          A large percentage of workforce reentry programs lost funding in 2025,
          leading to less indivduals served. For decades, the federal government
          has been a large and crucial investor in building our nation’s reentry
          workforce. Specifically, through the Department of Justice’s Second
          Chance Act and the Department of Labor’s Workforce Innovation and
          Opportunity Act (WIOA) programs, communities have been able to build
          systems that connect justice-involved individuals to jobs, training,
          and supports.  
        </p>
        <p>
          In 2025, changes in federal funding, such as delays or cancellations
          of certain grants, have resulted in program interruptions and
          decreased service capacity across the country. For justice-involved
          individuals, these changes can limit access to reentry support and
          slow progress toward successful reintegration. For communities, they
          can affect ongoing efforts to strengthen local partnerships, improve
          public safety, and expand economic participation.
        </p>
        <p>
          Although these proposals must still pass through Congress, if enacted
          they would represent some of the most significant reductions to
          reentry related funding in decades. Even in the event that the final
          FY 2026 budget restores a portion of this support, uncertainty remains
          over how executive agencies will choose to implement and prioritize
          spending within their approved allocations.
        </p>
      </div>
    </>
  );

  const topRightContent = (
    <>
      <p>
        The outlook for 2026 remains uncertain. Several executive agency
        proposed changes in the FY 2026 budget would have far-reaching
        implications for reentry and workforce programs, including:
      </p>
      <div className="federal-reductions-section-top-right-content">
        <div className="federal-reductions-section-stats-grid">
          {FEDERAL_REDUCTIONS_STATS_TOP.map((stat) => (
            <StatBlock
              stat={stat.stat}
              statPrefix={stat.statPrefix}
              statLabel={stat.statLabel}
              label={stat.label}
            />
          ))}
        </div>
      </div>
    </>
  );

  const bottomLeftContent = (
    <div className="federal-reductions-bottom-left-content">
      <img src={FEDERAL_REDUCTIONS_BOTTOM_LEFT_IMAGE} alt="" />
    </div>
  );

  const bottomRightContent = (
    <>
      <SectionHeader
        title="Federal Funding Disruptions: Justice Impacted Individuals and Communities"
        description="Funding cuts have cascading effects through communities. "
      />
      <div className="federal-reductions-bottom-right-content">
        <p>
          Respondents reported partner organizations shutting down or scaling
          back services such as housing assistance, utility support, food
          distribution, and recovery services. Several noted increased
          homelessness, reduced access to Medicaid and behavioral health care,
          and the closure of youth-focused initiatives like violence
          interruption programs. 
        </p>
        <p>
          Others described diminished capacity in education and training
          programs. Communities are also experiencing secondary effects such as
          rising competition for scarce resources, loss of local partnerships,
          and a greater sense of instability.
        </p>
        <p>
          Multiple respondents described how funding disruptions have removed
          critical support systems for justice-involved individuals. Without
          these services, individuals returning from incarceration are left with
          fewer pathways to stability, increasing the risk of recidivism. 
        </p>
        <p>
          Survey participants reported that the reductions on these services is
          fueling a sense of hopelessness, both for individuals trying to
          rebuild their lives and for the communities working to support them.
          These ripple effects illustrate that when reentry services contract,
          the harm extends well beyond program participants to the overall
          safety, cohesion, and economic health of the community.
        </p>
      </div>
    </>
  );

  return (
    <>
      <ContentSection className="federal-reductions-section">
        <TwoColumnLayout
          leftContent={topLeftContent}
          rightContent={topRightContent}
        />
      </ContentSection>
      <ContentSection className="federal-reductions-section-viz">
        <TwoColumnLayout
          leftContent={bottomLeftContent}
          rightContent={bottomRightContent}
        />
        <div className="federal-reductions-bottom-stats-grid">
          {FEDERAL_REDUCTIONS_STATS_BOTTOM.slice(0, 3).map((stat, idx) => (
            <StatBlock
              key={`bottom-stat-${idx}`}
              stat={stat.stat}
              statLabel={stat.statLabel}
              label={stat.label}
            />
          ))}
        </div>
      </ContentSection>
    </>
  );
};

export default FederalReductionsSection;
